import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const CauseRecommendationCard = ({ cause }) => {
  const navigate = useNavigate();

  const handleLearnMore = () => {
    navigate('/charity-profile');
  };

  const handleQuickDonate = (amount) => {
    console.log(`Quick donate $${amount} to ${cause?.name}`);
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'high': return 'text-error bg-error/10 border-error/20';
      case 'medium': return 'text-warning bg-warning/10 border-warning/20';
      case 'low': return 'text-success bg-success/10 border-success/20';
      default: return 'text-muted-foreground bg-muted border-border';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden shadow-elevation-1 hover:shadow-elevation-2 transition-smooth">
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <Image 
          src={cause?.image} 
          alt={cause?.name}
          className="w-full h-full object-cover hover-scale"
        />
        <div className="absolute top-3 left-3">
          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getUrgencyColor(cause?.urgency)}`}>
            {cause?.urgency === 'high' ? 'Urgent' : cause?.urgency === 'medium' ? 'Active' : 'Ongoing'}
          </span>
        </div>
        {cause?.isVerified && (
          <div className="absolute top-3 right-3">
            <div className="w-6 h-6 bg-success rounded-full flex items-center justify-center">
              <Icon name="Check" size={12} color="white" />
            </div>
          </div>
        )}
      </div>
      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold text-card-foreground text-lg leading-tight">{cause?.name}</h3>
          <Button variant="ghost" size="sm">
            <Icon name="Bookmark" size={16} />
          </Button>
        </div>

        <p className="text-sm text-muted-foreground mb-3">{cause?.category}</p>
        <p className="text-card-foreground text-sm mb-4 line-clamp-3">{cause?.description}</p>

        {/* Progress */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium text-card-foreground">
              ${cause?.raised?.toLocaleString()} of ${cause?.goal?.toLocaleString()}
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-500"
              style={{ width: `${Math.min((cause?.raised / cause?.goal) * 100, 100)}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground mt-1">
            <span>{Math.round((cause?.raised / cause?.goal) * 100)}% funded</span>
            <span>{cause?.donors} donors</span>
          </div>
        </div>

        {/* Impact Statement */}
        <div className="bg-muted/50 rounded-lg p-3 mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="Target" size={16} className="text-primary" />
            <p className="text-sm font-medium text-card-foreground">{cause?.impact}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-2">
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleQuickDonate(25)}
              className="flex-1 font-mono"
            >
              $25
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleQuickDonate(50)}
              className="flex-1 font-mono"
            >
              $50
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => handleQuickDonate(100)}
              className="flex-1 font-mono"
            >
              $100
            </Button>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLearnMore}
            className="w-full"
            iconName="ArrowRight"
            iconPosition="right"
          >
            Learn More
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CauseRecommendationCard;